import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { type QuestionAnswer, type RatingScaleQuestion } from "../../../../types";
import styles from "./RatingScale.module.scss";

type RatingScaleLabel = string | undefined | null;

const options = Array.from({ length: 10 }, (_, i) => i + 1);

interface Props {
  question: RatingScaleQuestion;
  setSelectedAnswers: (answers: QuestionAnswer[]) => void;
  selectedAnswers: QuestionAnswer[];
}

export const RatingScale = (props: Props) => {
  const { setSelectedAnswers, selectedAnswers, question } = props;

  const { text, minRateLabel, maxRateLabel } = question.question;

  const updateRating = useCallback((rating: number) => setSelectedAnswers([rating]), [setSelectedAnswers]);

  return (
    <div className={styles.answer}>
      <section className={styles.wrapper}>
        <div className={styles.ratings} role="radiogroup" aria-label={text}>
          {options.map(option => (
            <RatingOption
              key={option}
              value={option}
              isSelected={selectedAnswers.includes(option)}
              onClick={updateRating}
            />
          ))}
        </div>
        <RatingScaleLabels minRateLabel={minRateLabel} maxRateLabel={maxRateLabel} />
      </section>
    </div>
  );
};

const RatingOption = ({
  value,
  isSelected,
  onClick,
}: {
  value: number;
  isSelected: boolean;
  onClick: (value: number) => void;
}) => (
  <div className={`${styles.ratingOption} ${isSelected ? styles.selected : ""}`}>
    <button role="radio" aria-checked={isSelected} onClick={() => onClick(value)}>
      {value}
    </button>
  </div>
);

const RatingScaleLabels = ({
  minRateLabel,
  maxRateLabel,
}: {
  minRateLabel: RatingScaleLabel;
  maxRateLabel: RatingScaleLabel;
}) => {
  const { t } = useTranslation("assets");
  const minLabel = minRateLabel || t("common.leastLikely");
  const maxLabel = maxRateLabel || t("common.mostLikely");
  return (
    <div className={styles.ratingLabelsWrapper}>
      <span className={styles.label} aria-label={minLabel}>
        {minLabel}
      </span>
      <span className={styles.label} aria-label={maxLabel}>
        {maxLabel}
      </span>
    </div>
  );
};
